import axios from "axios";
import config from '../config';
import {
  PartnerAuthMode,
  PartnerCapability,
  PartnerIntegrationSchema,
  PartnerIntegrationStatus as PartnerIntegrationState,
} from '../data/types';

const new_api_host = config.apiUrl;
const secretKey = process.env.REACT_APP_SECRET_KEY ? process.env.REACT_APP_SECRET_KEY : ""
const apiKey = process.env.REACT_APP_API_KEY ? process.env.REACT_APP_API_KEY : ""

const axiosInstance = axios.create({
  baseURL: new_api_host,
  timeout: 100000,
  headers: {
    "Content-Type": "application/json",
    accept: "application/json",
  },
});

axiosInstance.defaults.headers.common['HOMECOOK-SECRET-KEY'] = secretKey
axiosInstance.defaults.headers.common['HOMECOOK-API-KEY'] = apiKey

// Integration Status Types
export type IntegrationStatus = {
  id: string;
  connected: boolean;
  connectedAt?: string;
  accountId?: string; // For QuickBooks, this is the realm_id
  accountName?: string; // Company name from QuickBooks
  lastSyncedAt?: string;
  error?: string;
  realmId?: string; // QuickBooks specific - may be returned by backend
}

// ==================== STRIPE INTEGRATION ====================
// (Already exists in stripeConnect.ts, but we'll add status check here)

export const getStripeIntegrationStatus = async (communityId: string, userId: string): Promise<IntegrationStatus> => {
  try {
    const response = await axiosInstance.get('/integrations/stripe/status', {
      params: { communityId, userId }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting Stripe integration status:', error);
    return { id: 'stripe', connected: false };
  }
};

// ==================== QUICKBOOKS INTEGRATION ====================

export const initiateQuickBooksOAuth = async (communityId: string, userId: string) => {
  try {
    // Backend now REQUIRES redirectUri - always send it
    // Ensure redirect URI matches exactly what's registered in QuickBooks app settings
    // The redirect URI must be URL-encoded and match exactly (including trailing slashes, query params, etc.)
    const redirectUri = `${config.dashboardURL}/settings/integrations?type=quickbooks`;
    
    console.log('Initiating QuickBooks OAuth with redirectUri:', redirectUri);
    
    const response = await axiosInstance.post('/integrations/quickbooks/oauth/initiate', {
      communityId,
      userId,
      redirectUri
    });
    
    if (!response.data?.authUrl) {
      throw new Error('Invalid response from OAuth initiate endpoint: missing authUrl');
    }
    
    return response.data;
  } catch (error: any) {
    console.error('Error initiating QuickBooks OAuth:', error);
    console.error('Error details:', {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status
    });
    
    // Backend now returns standardized error format: {"error": "message"}
    // Extract error message from response if available
    const errorMessage = error?.response?.data?.error || error?.message || 'Failed to initiate QuickBooks connection';
    const enhancedError = new Error(errorMessage);
    (enhancedError as any).response = error?.response;
    throw enhancedError;
  }
};

export const completeQuickBooksOAuth = async (
  communityId: string, 
  userId: string, 
  authCode: string, 
  realmId: string,
  redirectUri?: string
) => {
  try {
    // Build request body - include redirectUri if provided for consistency
    const requestBody: any = {
      communityId,
      userId,
      authCode,
      realmId
    };
    
    // Include redirectUri if provided (recommended for consistency with initiate request)
    if (redirectUri) {
      requestBody.redirectUri = redirectUri;
    }
    
    const response = await axiosInstance.post('/integrations/quickbooks/oauth/complete', requestBody);
    return response.data;
  } catch (error: any) {
    console.error('Error completing QuickBooks OAuth:', error);
    console.error('Error details:', {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status
    });
    
    // Backend now returns standardized error format: {"error": "message"}
    // Backend returns specific, user-friendly error messages that should be displayed directly
    const errorMessage = error?.response?.data?.error || error?.message || 'Failed to complete QuickBooks connection';
    const enhancedError = new Error(errorMessage);
    (enhancedError as any).response = error?.response;
    throw enhancedError;
  }
};

export const getQuickBooksIntegrationStatus = async (communityId: string, userId: string): Promise<IntegrationStatus> => {
  try {
    const response = await axiosInstance.get('/integrations/quickbooks/status', {
      params: { communityId, userId }
    });
    const status = response.data;
    // Log the response for debugging
    console.log('QuickBooks integration status response:', {
      connected: status?.connected,
      accountName: status?.accountName,
      accountId: status?.accountId,
      realmId: status?.realmId,
      lastSyncedAt: status?.lastSyncedAt,
      connectedAt: status?.connectedAt,
      warning: status?.warning,
      error: status?.error
    });
    // Ensure the response has the correct structure - backend returns camelCase
    // Backend may include a 'warning' field for non-critical issues - map it to error for display
    return {
      id: status?.id || 'quickbooks',
      connected: status?.connected || false,
      accountName: status?.accountName || null,
      accountId: status?.accountId || status?.realmId || null, // Support both accountId and realmId
      lastSyncedAt: status?.lastSyncedAt || null,
      connectedAt: status?.connectedAt || null,
      realmId: status?.realmId || status?.accountId || null,
      error: status?.error || status?.warning || null // Include warnings as errors for display
    };
  } catch (error: any) {
    console.error('Error getting QuickBooks integration status:', error);
    console.error('Error details:', {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status
    });
    return { id: 'quickbooks', connected: false, error: error?.response?.data?.error || error?.message };
  }
};

export const disconnectQuickBooks = async (communityId: string, userId: string) => {
  try {
    const response = await axiosInstance.delete('/integrations/quickbooks/disconnect', {
      params: { communityId, userId }
    });
    return response.data;
  } catch (error) {
    console.error('Error disconnecting QuickBooks:', error);
    throw error;
  }
};

// ==================== GOOGLE CALENDAR INTEGRATION ====================

export const initiateGoogleCalendarOAuth = async (communityId: string, userId: string) => {
  try {
    const response = await axiosInstance.post('/integrations/google/calendar/oauth/initiate', {
      communityId,
      userId,
      redirectUri: `${config.dashboardURL}/settings/integrations?type=google_calendar`
    });
    return response.data;
  } catch (error) {
    console.error('Error initiating Google Calendar OAuth:', error);
    throw error;
  }
};

export const completeGoogleCalendarOAuth = async (communityId: string, userId: string, authCode: string) => {
  try {
    const response = await axiosInstance.post('/integrations/google/calendar/oauth/complete', {
      communityId,
      userId,
      authCode
    });
    return response.data;
  } catch (error) {
    console.error('Error completing Google Calendar OAuth:', error);
    throw error;
  }
};

export const getGoogleCalendarIntegrationStatus = async (communityId: string, userId: string): Promise<IntegrationStatus> => {
  try {
    const response = await axiosInstance.get('/integrations/google/calendar/status', {
      params: { communityId, userId }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting Google Calendar integration status:', error);
    return { id: 'google_calendar', connected: false };
  }
};

export const syncGoogleCalendar = async (communityId: string, userId: string) => {
  try {
    const response = await axiosInstance.post('/integrations/google/calendar/sync', {
      communityId,
      userId
    });
    return response.data;
  } catch (error) {
    console.error('Error syncing Google Calendar:', error);
    throw error;
  }
};

export const disconnectGoogleCalendar = async (communityId: string, userId: string) => {
  try {
    const response = await axiosInstance.delete('/integrations/google/calendar/disconnect', {
      params: { communityId, userId }
    });
    return response.data;
  } catch (error) {
    console.error('Error disconnecting Google Calendar:', error);
    throw error;
  }
};

// ==================== GOOGLE DRIVE INTEGRATION ====================

export const initiateGoogleDriveOAuth = async (communityId: string, userId: string) => {
  try {
    const response = await axiosInstance.post('/integrations/google/drive/oauth/initiate', {
      communityId,
      userId,
      redirectUri: `${config.dashboardURL}/settings/integrations?type=google_drive`
    });
    return response.data;
  } catch (error) {
    console.error('Error initiating Google Drive OAuth:', error);
    throw error;
  }
};

export const completeGoogleDriveOAuth = async (communityId: string, userId: string, authCode: string) => {
  try {
    const response = await axiosInstance.post('/integrations/google/drive/oauth/complete', {
      communityId,
      userId,
      authCode
    });
    return response.data;
  } catch (error) {
    console.error('Error completing Google Drive OAuth:', error);
    throw error;
  }
};

export const getGoogleDriveIntegrationStatus = async (communityId: string, userId: string): Promise<IntegrationStatus> => {
  try {
    const response = await axiosInstance.get('/integrations/google/drive/status', {
      params: { communityId, userId }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting Google Drive integration status:', error);
    return { id: 'google_drive', connected: false };
  }
};

export const listGoogleDriveFiles = async (communityId: string, userId: string, folderId?: string) => {
  try {
    const response = await axiosInstance.get('/integrations/google/drive/files', {
      params: { communityId, userId, folderId }
    });
    return response.data;
  } catch (error) {
    console.error('Error listing Google Drive files:', error);
    throw error;
  }
};

export const listGoogleSheets = async (communityId: string, userId: string) => {
  try {
    const response = await axiosInstance.get('/integrations/google/drive/sheets', {
      params: { communityId, userId }
    });
    return response.data;
  } catch (error) {
    console.error('Error listing Google Sheets:', error);
    throw error;
  }
};

export const disconnectGoogleDrive = async (communityId: string, userId: string) => {
  try {
    const response = await axiosInstance.delete('/integrations/google/drive/disconnect', {
      params: { communityId, userId }
    });
    return response.data;
  } catch (error) {
    console.error('Error disconnecting Google Drive:', error);
    throw error;
  }
};

// ==================== MAILCHIMP INTEGRATION ====================

export const initiateMailchimpOAuth = async (communityId: string, userId: string) => {
  try {
    const response = await axiosInstance.post('/integrations/mailchimp/oauth/initiate', {
      communityId,
      userId,
      redirectUri: `${config.dashboardURL}/settings/integrations?type=mailchimp`
    });
    return response.data;
  } catch (error) {
    console.error('Error initiating Mailchimp OAuth:', error);
    throw error;
  }
};

export const completeMailchimpOAuth = async (communityId: string, userId: string, authCode: string) => {
  try {
    const response = await axiosInstance.post('/integrations/mailchimp/oauth/complete', {
      communityId,
      userId,
      authCode
    });
    return response.data;
  } catch (error) {
    console.error('Error completing Mailchimp OAuth:', error);
    throw error;
  }
};

export const getMailchimpIntegrationStatus = async (communityId: string, userId: string): Promise<IntegrationStatus> => {
  try {
    const response = await axiosInstance.get('/integrations/mailchimp/status', {
      params: { communityId, userId }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting Mailchimp integration status:', error);
    return { id: 'mailchimp', connected: false };
  }
};

export const getMailchimpLists = async (communityId: string, userId: string) => {
  try {
    const response = await axiosInstance.get('/integrations/mailchimp/lists', {
      params: { communityId, userId }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting Mailchimp lists:', error);
    throw error;
  }
};

export const syncMailchimpLists = async (communityId: string, userId: string) => {
  try {
    const response = await axiosInstance.post('/integrations/mailchimp/sync', {
      communityId,
      userId
    });
    return response.data;
  } catch (error) {
    console.error('Error syncing Mailchimp lists:', error);
    throw error;
  }
};

export const disconnectMailchimp = async (communityId: string, userId: string) => {
  try {
    const response = await axiosInstance.delete('/integrations/mailchimp/disconnect', {
      params: { communityId, userId }
    });
    return response.data;
  } catch (error) {
    console.error('Error disconnecting Mailchimp:', error);
    throw error;
  }
};

// ==================== HUBSPOT INTEGRATION ====================

export const initiateHubSpotOAuth = async (communityId: string, userId: string) => {
  try {
    const response = await axiosInstance.post('/integrations/hubspot/oauth/initiate', {
      communityId,
      userId,
      redirectUri: `${config.dashboardURL}/settings/integrations?type=hubspot`
    });
    return response.data;
  } catch (error) {
    console.error('Error initiating HubSpot OAuth:', error);
    throw error;
  }
};

export const completeHubSpotOAuth = async (communityId: string, userId: string, authCode: string) => {
  try {
    const response = await axiosInstance.post('/integrations/hubspot/oauth/complete', {
      communityId,
      userId,
      authCode
    });
    return response.data;
  } catch (error) {
    console.error('Error completing HubSpot OAuth:', error);
    throw error;
  }
};

export const getHubSpotIntegrationStatus = async (communityId: string, userId: string): Promise<IntegrationStatus> => {
  try {
    const response = await axiosInstance.get('/integrations/hubspot/status', {
      params: { communityId, userId }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting HubSpot integration status:', error);
    return { id: 'hubspot', connected: false };
  }
};

export const getHubSpotContacts = async (communityId: string, userId: string) => {
  try {
    const response = await axiosInstance.get('/integrations/hubspot/contacts', {
      params: { communityId, userId }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting HubSpot contacts:', error);
    throw error;
  }
};

export const syncHubSpotContacts = async (communityId: string, userId: string) => {
  try {
    const response = await axiosInstance.post('/integrations/hubspot/sync', {
      communityId,
      userId
    });
    return response.data;
  } catch (error) {
    console.error('Error syncing HubSpot contacts:', error);
    throw error;
  }
};

export const disconnectHubSpot = async (communityId: string, userId: string) => {
  try {
    const response = await axiosInstance.delete('/integrations/hubspot/disconnect', {
      params: { communityId, userId }
    });
    return response.data;
  } catch (error) {
    console.error('Error disconnecting HubSpot:', error);
    throw error;
  }
};

// ==================== GENERAL INTEGRATION HELPERS ====================

export const getAllIntegrationStatuses = async (communityId: string, userId: string): Promise<IntegrationStatus[]> => {
  try {
    const response = await axiosInstance.get('/integrations/status', {
      params: { communityId, userId }
    });
    const statuses = response.data || [];
    // Log QuickBooks status specifically for debugging
    const quickbooksStatus = statuses.find((s: IntegrationStatus) => s.id === 'quickbooks');
    if (quickbooksStatus) {
      console.log('QuickBooks status from getAllIntegrationStatuses:', {
        connected: quickbooksStatus.connected,
        accountName: quickbooksStatus.accountName,
        accountId: quickbooksStatus.accountId,
        realmId: (quickbooksStatus as any).realmId,
        lastSyncedAt: quickbooksStatus.lastSyncedAt,
        connectedAt: quickbooksStatus.connectedAt
      });
    }
    // Normalize the response to ensure consistent structure
    // Backend returns camelCase fields: accountName, connectedAt, lastSyncedAt, accountId, realmId
    return statuses.map((status: any) => ({
      id: status.id || status.integration_id,
      connected: status.connected || false,
      accountName: status.accountName || null,
      accountId: status.accountId || status.realmId || null,
      lastSyncedAt: status.lastSyncedAt || null,
      connectedAt: status.connectedAt || null,
      realmId: status.realmId || status.accountId || null,
      error: status.error || status.warning || null // Include warnings for display
    }));
  } catch (error: any) {
    console.error('Error getting all integration statuses:', error);
    console.error('Error details:', {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status
    });
    return [];
  }
};

export type PartnerIntegrationStatus = PartnerIntegrationSchema & {
  connected?: boolean
  status?: PartnerIntegrationState
  capabilities?: PartnerCapability[]
  auth_mode?: PartnerAuthMode
  signup_url?: string
  fulfillment_mode?: string
  /** Returned once after rotation; callers must keep it in volatile memory only. */
  webhook_secret?: string
}

export type PartnerIntegrationUpdate = {
  webhook_url?: string
  signup_url?: string
  fulfillment_mode?: string
  status?: PartnerIntegrationState
  capabilities?: PartnerCapability[]
  auth_mode?: PartnerAuthMode
  resource_id?: string
};

/** Load a financial-partner integration record. */
export const getPartnerIntegrationStatus = async (communityId: string, slug = 'airiwheels'): Promise<PartnerIntegrationStatus> => {
  const response = await axiosInstance.get(`/community/${communityId}/integrations/partners/${slug}`);
  return response.data;
};

/** Connect or update partner webhook settings. */
export const updatePartnerIntegration = async (
  communityId: string,
  slug: string,
  body: PartnerIntegrationUpdate
) => {
  const response = await axiosInstance.patch(`/community/${communityId}/integrations/partners/${slug}`, body);
  return response.data as PartnerIntegrationStatus;
};

/** Rotate HMAC secret and return the plaintext once. */
export const rotatePartnerWebhookSecret = async (communityId: string, slug = 'airiwheels') => {
  const response = await axiosInstance.post(`/community/${communityId}/integrations/partners/${slug}/rotate-secret`);
  return response.data as PartnerIntegrationStatus;
};

/** Stop future partner sync while retaining independently required records. */
export const disconnectPartnerIntegration = async (
  communityId: string,
  slug = 'airiwheels',
  state: Extract<PartnerIntegrationState, 'disconnected' | 'revoked'> = 'disconnected'
) => updatePartnerIntegration(communityId, slug, { status: state });

/** Stub OAuth initiate when a partner publishes authorize URLs. */
export const initiatePartnerOAuth = async (communityId: string, slug: string) => {
  const status = await getPartnerIntegrationStatus(communityId, slug);
  if (!status.oauth_authorize_url) {
    throw new Error('This partner has not published an OAuth authorize URL yet.');
  }
  return { authUrl: status.oauth_authorize_url };
};

